import React, {createContext, useState} from 'react';

/**
 * Props for the `AuthProvider` component.
 */
export type AuthProviderProps = {
  /** The children components */
  children: React.ReactNode;
};

// These default values will never be used, as the AuthProvider will always be used
const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  logout: () => {}
});

/**
 * Authentication provider component. The authentication state and functions are provided to all children, so all
 * components should be wrapped in this component.
 *
 * @param props - Props for the AuthProvider component
 * @category Component
 */
const AuthProvider = ({children}: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if the user was authenticated before
    const savedAuthState = localStorage.getItem('isAuthenticated');
    return savedAuthState === 'true';
  });
  const login = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
  };
  const logout = () => {
    setIsAuthenticated(false);
    localStorage.setItem('isAuthenticated', 'false');
  };

  return (
    // The AuthContext.Provider component will provide the authentication state and functions to all children
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// The useAuth hook can be used to access the authentication state and functions
export const useAuth = () => React.useContext(AuthContext);

export default AuthProvider;